import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreateProject, useOrders, useServiceItems, useLocations } from '@t90/api-client';
import type { Order, Location } from '@t90/common/supabase';
import {
  Button,
  Input,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ArrowLeft,
  Loader2,
  Save,
} from '@t90/ui';

// Form validation schema - matching actual database schema
const newProjectSchema = z.object({
  order_id: z.string().min(1, 'Order is required'),
  service_item_id: z.string().min(1, 'Service item is required'),
  quantity: z.string().min(1, 'Quantity is required'),
  location_id: z.string().optional(),
  start_date: z.string().optional(),
  due_date: z.string().optional(),
  notes: z.string().optional(),
});

type NewProjectFormData = z.infer<typeof newProjectSchema>;

export function NewProjectPage() {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const { data: orders, isLoading: ordersLoading } = useOrders();
  const { data: serviceItems, isLoading: servicesLoading } = useServiceItems();
  const { data: locations, isLoading: locationsLoading } = useLocations({ active: true });

  // Cast data to proper types
  const orderList = (orders ?? []) as (Order & { customer?: { customer_name: string } })[];
  const serviceItemList = (serviceItems ?? []) as { id: string; service_item_name: string; service_type?: { service_type_name: string } }[];
  const locationList = (locations ?? []) as Location[];

  const form = useForm<NewProjectFormData>({
    resolver: zodResolver(newProjectSchema),
    defaultValues: {
      order_id: '',
      service_item_id: '',
      quantity: '',
      location_id: '',
      start_date: '',
      due_date: '',
      notes: '',
    },
  });

  async function onSubmit(data: NewProjectFormData) {
    try {
      const projectData = {
        order_id: data.order_id,
        service_item_id: data.service_item_id,
        quantity: parseInt(data.quantity, 10),
        location_id: data.location_id || null,
        start_date: data.start_date || null,
        due_date: data.due_date || null,
        notes: data.notes || null,
      };

      const result = await createProject.mutateAsync(projectData);
      navigate(`/projects/${result.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  }

  const isLoading = ordersLoading || servicesLoading || locationsLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">New Project</h1>
          <p className="text-muted-foreground">
            Create a new project for an order
          </p>
        </div>
      </div>

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Order & Service Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
              <CardDescription>
                Link this project to an order and select the service
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="order_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Order *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an order" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {orderList.map((order) => (
                          <SelectItem key={order.id} value={order.id}>
                            {order.order_number} - {order.customer?.customer_name ?? 'Unknown Customer'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The order this project belongs to
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="service_item_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Service Item *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a service item" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {serviceItemList.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.service_item_name} {item.service_type ? `(${item.service_type.service_type_name})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The type of service for this project
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantity *</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" placeholder="100" {...field} />
                    </FormControl>
                    <FormDescription>
                      Number of units to complete
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Schedule & Location */}
          <Card>
            <CardHeader>
              <CardTitle>Schedule & Assignment</CardTitle>
              <CardDescription>
                Set target dates and assign to a location
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>
                        When should work begin?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Due Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>
                        When should this project be completed?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="location_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={locationsLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Assign to location" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {locationList.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.location_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Which location will work on this project?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any additional notes about this project..."
                        className="min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
            <CardFooter className="flex justify-between border-t pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/projects')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createProject.isPending}>
                {createProject.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Create Project
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}
